import type { ReactNode } from 'react';
import React from 'react';
import type { TooltipProps as AntdTooltipProps } from 'antd';
import { Tooltip as AntdTooltip } from 'antd';
import { useIntl } from 'react-intl';
import type { TooltipPlacement } from 'antd/lib/tooltip';
import isString from 'lodash/isString';
import { ClassNames, css } from '@emotion/react';
import styled from 'types/theme.types';

export type TooltipProps = {
  title?: string | ReactNode | (() => ReactNode);
  titleId?: string;
  titleValues?: Record<string, any>;
  placement: TooltipPlacement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // left undefined by default so existing callers keep antd's hover-only behaviour
  trigger?: AntdTooltipProps['trigger'];
  mouseEnterDelay?: AntdTooltipProps['mouseEnterDelay'];
  children?: ReactNode;
};

/**
 * `overlayClassName` lands on `.ant-tooltip`, and antd's `:where(...)` selectors carry zero
 * specificity - so of these, only `max-width` needs doubling, being the one antd declares there.
 *
 * The link colour moves here from antd-wrapper's <Global> block.
 */
const overlayCss = css`
  word-break: break-word;

  && {
    max-width: 320px;
  }

  /* A primitive, because the spotlight is a fixed dark olive in both colour modes and no semantic token
     means "link over the spotlight" (same reason the Tooltip block in antd.constants pins
     colorTextLightSolid). --text-action is lime-700 in both modes: 2.94:1 here, under AA. Raised with
     design. The hover selector is written out to outrank link.tsx's doubled-class hover. */
  .ant-tooltip-inner a,
  .ant-tooltip-inner a:hover {
    color: var(--color-lime-400);
  }
`;

const Tooltip: React.FunctionComponent<TooltipProps> = ({
  title,
  titleId,
  children,
  placement,
  titleValues,
  open,
  onOpenChange,
  trigger,
  mouseEnterDelay
}) => {
  const intl = useIntl();

  const antdTitle = titleId ? intl.formatMessage({ id: titleId }, titleValues) : (title ?? '');

  const formattedTitle = antdTitle && isString(antdTitle) ? <StyledTooltip>{antdTitle}</StyledTooltip> : antdTitle;

  return (
    // antd wants a className string, so the class has to be materialised. ClassNames is emotion's way
    // to do that without @emotion/css, which the lint rule bans for needing a CSP `unsafe-inline`.
    <ClassNames>
      {({ css: toClassName }) => (
        <AntdTooltip
          overlayClassName={toClassName(overlayCss)}
          title={formattedTitle}
          placement={placement}
          open={open}
          onOpenChange={onOpenChange}
          mouseEnterDelay={mouseEnterDelay}
          trigger={trigger}>
          {children}
        </AntdTooltip>
      )}
    </ClassNames>
  );
};

export default Tooltip;

const StyledTooltip = styled.div`
  max-height: 360px;
  overflow-y: auto;
  white-space: pre-wrap;
`;
